/**
 * Which plants a new company user works for (owner's review, 14 Oct 2026).
 *
 * The four company roles: Company Admin (the owner) runs the whole company;
 * HR and Accounts always work for a plant; EHS sees every plant or, given one,
 * only that plant's data.
 * Mirrors PLANT_LIMITABLE_ROLES in apps/api/src/rbac/services/binding-compat.ts.
 */
export const PLANT_LIMITABLE_ROLES: ReadonlySet<string> = new Set(['EHS_VIEWER']);

/** `none`: no plant to choose. `required`: one plant. `optional`: a plant, or all of them. */
export type PlantAccessMode = 'none' | 'required' | 'optional';

export const ALL_PLANTS = 'ALL';

export function plantAccessMode(role: { key: string; scopeType?: string } | undefined, scopeType?: string): PlantAccessMode {
  if (!role) return 'none';
  if ((role.scopeType ?? scopeType) === 'PLANT') return 'required';
  return PLANT_LIMITABLE_ROLES.has(role.key) ? 'optional' : 'none';
}

/** A company with one plant needs no choice: the user works there. */
export function initialPlantAccess(mode: PlantAccessMode, plantIds: string[]): string {
  if (mode === 'none') return '';
  if (plantIds.length === 1) return plantIds[0]!;
  if (mode === 'optional' && plantIds.length === 0) return ALL_PLANTS;
  return '';
}

/** What the API takes: the plant, or nothing for the whole company. */
export function scopeIdsFor(mode: PlantAccessMode, value: string): string[] {
  return mode !== 'none' && value && value !== ALL_PLANTS ? [value] : [];
}

export function plantAccessReady(mode: PlantAccessMode, value: string): boolean {
  return mode === 'none' || value !== '';
}
